import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import ModalContext from 'contexts/ModalContext'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import type { MappedRequestAgentCallbackResult } from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import { formatCallbackTimeLabel } from 'supportAssistant/components/RequestAgentCallback/RequestAgentCallbackUtils'

interface UseScheduleCallbackChatActionInput {
  submitMessage: (message: string) => Promise<void> | void
  onOpen?: () => void
  messagePlaceholder?: string
}

export function useScheduleCallbackChatAction({
  submitMessage,
  onOpen,
  messagePlaceholder,
}: UseScheduleCallbackChatActionInput) {
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const showModal = useContext(ModalContext)

  const handleCallbackRequestSubmitted = useCallback(async(results: MappedRequestAgentCallbackResult) => {
    const { callbackDate, callbackTime } = results.customer
    const formattedTime = formatCallbackTimeLabel(callbackTime, intlDateFormatter)
    const formattedDate = callbackDate
      ? intlDateFormatter(callbackDate, INTL_DMY_CASUAL_FORMAT)
      : ''

    if (!formattedTime || !formattedDate) {
      return
    }

    await submitMessage(
      intl.formatMessage(
        {
          id: '1JIZSmwFqLHg37YN0HnK',
          defaultMessage: 'I scheduled a callback for {time} on {date}.',
          description: 'User chat message sent automatically after successfully scheduling a callback in trip planner chat',
        },
        {
          time: formattedTime,
          date: formattedDate,
        },
      ),
    )
  }, [intl, intlDateFormatter, submitMessage])

  return useCallback(() => {
    const defaultMessagePlaceholder = intl.formatMessage({
      id: 'i-4TtHQw-VV2kmnT-QDB',
      defaultMessage: "e.g. I'd love help turning my itinerary into a real trip",
      description: 'Custom message placeholder shown in the request callback modal when opened from trip planner chat',
    })

    onOpen?.()
    showModal(
      <RequestAgentCallbackModal
        type="SALES"
        onRequestSubmitted={handleCallbackRequestSubmitted}
        messagePlaceholder={messagePlaceholder ?? defaultMessagePlaceholder}
      />,
    )
  }, [handleCallbackRequestSubmitted, intl, messagePlaceholder, onOpen, showModal])
}
